/**
 * Custom Hook: useHoldingCompanies
 * Loads holding companies for the authenticated user on mount
 * Provides companies list and loading state
 */

import { useEffect, useState } from 'react';
import { holdingService } from '../holding.service';
import type { HoldingCompany } from '../holding.types';
import { useUI } from '@/hooks/ui/useUI';

/**
 * Hook to fetch and manage holding companies state
 * Automatically loads companies on mount
 * @returns Object with companies array and loading boolean
 */
export function useHoldingCompanies({ enabled = true }: { enabled?: boolean } = {}) {
  const [companies, setCompanies] = useState<HoldingCompany[]>([]);
  const [loading, setLoading] = useState(true);
  const { showNotification } = useUI();

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }

    let isMounted = true;

    const loadCompanies = async () => {
      try {
        const response = await holdingService.list();

        if (!isMounted) return;

        if (!response.success || !response.data) {
          showNotification('error', 'No se pudieron obtener empresas', 5000);
          setCompanies([]);
          setLoading(false);
          return;
        }

        setCompanies(response.data);
        setLoading(false);
      } catch {
        if (!isMounted) return;
        showNotification('error', 'Error inesperado obteniendo empresas', 5000);
        setCompanies([]);
        setLoading(false);
      }
    };

    void loadCompanies();

    return () => {
      isMounted = false;
    };
  }, [enabled, showNotification]);

  return { companies, loading };
}
